import { useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '../../../components/ui/use-toast.tsx';
import { useLocale } from '../../../lib/hooks/use-locale.ts';
import type { SalesDraftDetailOutput } from '../../../server/types/drafts-types.ts';
import { api } from '../../../utils/api.ts';
import { useAnalytics } from '../../analytics/hooks/use-analytics.ts';
import { DialogForm } from '../../dialog/dialog-form.tsx';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import type { DialogInnerProps } from '../../dialog/types.ts';
import type { DraftOrderFormProps } from './draft-order-form.tsx';
import { DraftOrderForm } from './draft-order-form.tsx';

type DraftOrderCreateDialogProps = {
    onSuccess: (data: SalesDraftDetailOutput) => void;
} & DraftOrderFormProps &
    DialogInnerProps;

export const DraftOrderCreateDialog = DialogStateManager.create(
    ({ customerId, storageId, onSuccess }: DraftOrderCreateDialogProps) => {
        const dialog = useDialog();
        const { t } = useLocale();
        const { toast } = useToast();

        const sendEvent = useAnalytics();

        useEffect(() => {
            sendEvent('transaction_creation_started', { customer_id: customerId });
        }, [sendEvent, customerId]);

        const { mutate, isLoading } = api.platoUser.command.salesDraft.draft.create.useMutation({
            onSuccess(data) {
                onSuccess(data);
                dialog.remove();
            },
            onError(error) {
                console.error('onError', error);

                toast({
                    title: 'error',
                    description: error.message,
                    icon: 'warning',
                });
            },
        });

        return (
            <Dialog open={dialog.visible} onClose={dialog.remove} showClose>
                <DialogForm className="max-w-2xl">
                    <DialogForm.Header title={t('sales_draft__new_form___title')} />
                    <DraftOrderForm
                        customerId={customerId}
                        storageId={storageId}
                        onSubmit={(data) => {
                            mutate({
                                draft_order_id: uuidv4(),
                                ...data,
                            });
                        }}
                        onCancel={dialog.remove}
                        isLoading={isLoading}
                    />
                </DialogForm>
            </Dialog>
        );
    },
);
